/**
 * <table-of-contents> — scans h2 inside data-target, builds anchor list,
 * highlights the active section on scroll. Hides itself if no h2 found.
 *
 * <share-buttons> — adds copy-to-clipboard for the [data-share-copy] button.
 */

class TableOfContents extends HTMLElement {
  connectedCallback() {
    this.targetSelector = this.dataset.target;
    this.list = this.querySelector('[data-toc-list]');
    if (!this.targetSelector || !this.list) return;

    this.target = document.querySelector(this.targetSelector);
    if (!this.target) return;

    this.headings = Array.from(this.target.querySelectorAll('h2'));
    if (this.headings.length === 0) {
      this.hidden = true;
      return;
    }

    this.buildList();
    this.observeActive();
  }

  buildList() {
    const fragment = document.createDocumentFragment();

    this.headings.forEach((heading, index) => {
      if (!heading.id) heading.id = `toc-${index}-${this.slugify(heading.textContent)}`;

      const li = document.createElement('li');
      li.className = 'article-body__toc-item';

      const link = document.createElement('a');
      link.className = 'article-body__toc-link';
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      link.dataset.tocId = heading.id;

      li.appendChild(link);
      fragment.appendChild(li);
    });

    this.list.replaceChildren(fragment);
  }

  observeActive() {
    if (!('IntersectionObserver' in window)) return;

    this.links = new Map();
    this.querySelectorAll('[data-toc-id]').forEach((link) => {
      this.links.set(link.dataset.tocId, link);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.setActive(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' },
    );

    this.headings.forEach((heading) => observer.observe(heading));
  }

  setActive(id) {
    this.links.forEach((link, key) => {
      link.dataset.active = key === id ? 'true' : 'false';
    });
  }

  slugify(text) {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40);
  }
}

if (!customElements.get('table-of-contents')) {
  customElements.define('table-of-contents', TableOfContents);
}

class ShareButtons extends HTMLElement {
  connectedCallback() {
    this.copyButton = this.querySelector('[data-share-copy]');
    if (!this.copyButton) return;

    this.url = this.dataset.url || window.location.href;
    this.copyButton.addEventListener('click', this.handleCopy.bind(this));
  }

  async handleCopy() {
    try {
      await navigator.clipboard.writeText(this.url);
      this.copyButton.dataset.copied = 'true';
      setTimeout(() => {
        this.copyButton.dataset.copied = 'false';
      }, 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  }
}

if (!customElements.get('share-buttons')) {
  customElements.define('share-buttons', ShareButtons);
}
